import { ArrowLeftOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Select,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { debounce } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import AccessControl from '../../../AccessControl';
import { AppContext } from '../../../AppContext';
import {
  APP_BUILD_ENVIRONMENTS,
  APP_BUILD_REVIEW_STATUS,
  APP_TYPES,
  MODULES_KEY,
  PERMISSION_OBJECT,
  REGEX,
  ROLLOUT_MODES,
  SORT_BY,
  SUBMISSION_MODES,
} from '../../../common/constants';
import CommonSelect from '../../../components/CommonSelect';
import LoaderComponent from '../../../components/LoaderComponent';
import VerifyPermissions from '../../../components/VerifyPermissions';
import { PROJECT_FOR_RELEASE } from '../../releases/graphql/Queries';
import { CREATE_APP_BUILD, UPDATE_APP_BUILD } from '../graphql/Mutations';
import { APP_BUILD, APP_BUILD_ASSIGNEES } from '../graphql/Queries';
import MaskedCredentialsInput from './MaskedCredentialsInput';
import StatusBadge from './StatusBadge';

function ViewAppBuild() {
  const canUpdate = VerifyPermissions({
    modulekey: MODULES_KEY?.APP_BUILDS,
    allowedPermissions: [PERMISSION_OBJECT?.update],
  });
  const canDelete = VerifyPermissions({
    modulekey: MODULES_KEY?.APP_BUILDS,
    allowedPermissions: [PERMISSION_OBJECT?.delete],
  });
  const canCreate = VerifyPermissions({
    modulekey: MODULES_KEY?.APP_BUILDS,
    allowedPermissions: [PERMISSION_OBJECT?.create],
  });
  const history = useHistory();
  const location = useLocation();
  const { buildId } = useParams();
  const [form] = Form.useForm();
  const {
    state: { currentUser },
    dispatch,
  } = useContext(AppContext);

  const loggedInUserName = useMemo(
    () =>
      currentUser?.displayName ||
      `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() ||
      '-',
    [currentUser],
  );

  const loggedInUserEmail = useMemo(() => currentUser?.email || '-', [
    currentUser,
  ]);

  const [buildData, setBuildData] = useState(null);
  const [editableButton, setEditableButton] = useState(false);
  const [appNameSearch, setAppNameSearch] = useState('');
  const [webhookMissing, setWebhookMissing] = useState(false);
  const watchAppName = Form.useWatch('appName', form);
  // "Unavailable" means no build was ever submitted for that platform — not
  // the app's declared appType — so on reopening a build where only Android
  // was filled in, the iOS section (including Review Status) locks down.
  const hasAndroidDetails = !!(
    buildData?.androidAppVersion ||
    buildData?.androidVersionCode ||
    buildData?.apkTestLink ||
    buildData?.aabSubmissionLink ||
    buildData?.androidSubmissionMode ||
    buildData?.androidRolloutMode
  );
  const hasIosDetails = !!(
    buildData?.iosAppVersion ||
    buildData?.iosBuildNumber ||
    buildData?.ipaTestLink ||
    buildData?.iosSubmissionMode ||
    buildData?.iosRolloutMode
  );
  const isAndroidUnavailable = !!buildId && !hasAndroidDetails;
  const isIosUnavailable = !!buildId && !hasIosDetails;

  // A user (e.g. Mobile Developer) whose APP_BUILDS access is only view +
  // create (no update, no delete) can only set the review status to PENDING.
  // With any higher permission they can pick any status. Note: such users
  // carry role "USER" with a "Mobile Developer" permission role, so this is
  // gated on the actual APP_BUILDS access, not currentUser.role.
  const isRestrictedMobileDev = canCreate && !canUpdate && !canDelete;
  const reviewStatusOptions = useMemo(
    () =>
      isRestrictedMobileDev
        ? APP_BUILD_REVIEW_STATUS.filter((opt) => opt?.value === 'PENDING')
        : APP_BUILD_REVIEW_STATUS,
    [isRestrictedMobileDev],
  );

  const resolveOption = (options, val) =>
    options?.find((opt) => opt?.value === val || opt?.label === val)?.value;

  const { loading } = useQuery(APP_BUILD, {
    variables: { appBuildId: buildId },
    fetchPolicy: 'network-only',
    skip: !buildId,
    onCompleted: (res) => {
      const appBuild = res?.appBuild;
      if (appBuild) {
        setBuildData(appBuild);
        form?.setFieldsValue({
          ...appBuild,
          appName: appBuild?.projectId
            ? { value: appBuild?.projectId, label: appBuild?.appName }
            : appBuild?.appName,
          appType: resolveOption(APP_TYPES, appBuild?.appType),
          environment: resolveOption(
            APP_BUILD_ENVIRONMENTS,
            appBuild?.environment,
          ),
          androidStatus: resolveOption(
            APP_BUILD_REVIEW_STATUS,
            appBuild?.androidStatus,
          ),
          iosStatus: resolveOption(
            APP_BUILD_REVIEW_STATUS,
            appBuild?.iosStatus,
          ),
          androidSubmissionMode: resolveOption(
            SUBMISSION_MODES,
            appBuild?.androidSubmissionMode,
          ),
          androidRolloutMode: resolveOption(
            ROLLOUT_MODES,
            appBuild?.androidRolloutMode,
          ),
          iosSubmissionMode: resolveOption(
            SUBMISSION_MODES,
            appBuild?.iosSubmissionMode,
          ),
          iosRolloutMode: resolveOption(
            ROLLOUT_MODES,
            appBuild?.iosRolloutMode,
          ),
          assigneeId: appBuild?.assigneeId
            ? { value: appBuild?.assigneeId, label: appBuild?.assigneeName }
            : undefined,
          requesterName: appBuild?.requesterName || loggedInUserName,
          requesterEmail: appBuild?.requesterEmail || loggedInUserEmail,
        });
      }
    },
  });

  useEffect(() => {
    if (!buildId) {
      const releaseRecord = location?.state?.releaseRecord;
      form?.resetFields();
      form?.setFieldsValue({
        appName: releaseRecord
          ? {
            value: releaseRecord?.projectId,
            label: releaseRecord?.projectName,
          }
          : undefined,
        requesterName: loggedInUserName,
        requesterEmail: loggedInUserEmail,
        androidAppVersion: releaseRecord?.releaseNumber || '',
        iosAppVersion: releaseRecord?.releaseNumber || '',
      });
    }
  }, [form, buildId, loggedInUserName, loggedInUserEmail, location?.state]);

  const [executeProjectQuery] = useLazyQuery(PROJECT_FOR_RELEASE, {
    fetchPolicy: 'network-only',
    onCompleted(res) {
      const projectId = watchAppName?.value || watchAppName;
      const projects = res?.projectsForRelease?.projects || [];
      const matchedProject =
        projects?.find((project) => project?.id === projectId) ||
        projects?.[0];
      setWebhookMissing(!matchedProject?.gchatWebhookUrl);
    },
    onError() {
      setWebhookMissing(false);
    },
  });

  useEffect(() => {
    const projectId = watchAppName?.value || watchAppName;
    const projectName = watchAppName?.label;
    if (projectId) {
      executeProjectQuery({
        variables: { filters: { searchString: projectName } },
      });
    } else {
      setWebhookMissing(false);
    }
  }, [watchAppName]);

  const [createAppBuild, { loading: createLoading }] = useMutation(
    CREATE_APP_BUILD,
    {
      onCompleted: () => {
        history?.goBack();
      },
      onError: () => { },
    },
  );

  const [updateAppBuild, { loading: updateLoading }] = useMutation(
    UPDATE_APP_BUILD,
    {
      onCompleted: () => {
        setEditableButton(false);
        history?.goBack();
      },
      onError: () => { },
    },
  );

  const mutationLoading = createLoading || updateLoading;

  const onFinish = (values) => {
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: false,
    });
    const input = {
      ...values,
      appName: values?.appName?.label || buildData?.appName,
      assigneeId: values?.assigneeId?.value || values?.assigneeId,
      assigneeName: values?.assigneeId?.label || buildData?.assigneeName,
      projectId: values?.appName?.value || buildData?.projectId,
    };
    if (buildId) {
      updateAppBuild({
        variables: { id: buildId, input },
      });
    } else {
      createAppBuild({
        variables: { input },
      });
    }
  };

  const handleBack = () => {
    history?.goBack();
  };

  const handleAppNameSearch = debounce((value) => {
    setAppNameSearch(value);
  }, 500);

  const renderLink = (url) => {
    if (!url) return '-';
    return (
      <a href={url} target="_blank" rel="noopener noreferrer">
        {url}
      </a>
    );
  };

  return (
    <>
      {loading && <LoaderComponent />}
      <Card
        size="small"
        className="ant-body-scroll"
        title={
          <div className="d-flex align-center">
            <Button
              type="text"
              shape="square"
              onClick={handleBack}
              icon={<ArrowLeftOutlined />}
            />
            {buildId ? 'App Build Details' : 'Create App Build'}
          </div>
        }
        extra={
          <>
            <div className="users-sidebar">
              <div className="user-filter-right">
                <div className="user-filter">
                  {buildId ? (
                    <AccessControl
                      modulekey={MODULES_KEY?.APP_BUILDS}
                      allowedPermissions={[PERMISSION_OBJECT?.update]}
                    >
                      <Button
                        type="primary"
                        onClick={() => form?.submit()}
                        disabled={!editableButton}
                        loading={mutationLoading}
                      >
                        Save
                      </Button>
                    </AccessControl>
                  ) : (
                    <Button
                      type="primary"
                      onClick={() => form?.submit()}
                      loading={mutationLoading}
                    >
                      Save
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </>
        }
      >
        <div className="card-body-wrapper user-detail-page">
          <div className="card-padding">
            <Form
              name="control-ref"
              form={form}
              layout="vertical"
              onFinish={onFinish}
              disabled={buildId ? !canUpdate : false}
              onFieldsChange={() => {
                if (form?.isFieldsTouched()) {
                  setEditableButton(true);
                  dispatch({
                    type: 'SET_SHOW_PROMPT',
                    data: true,
                  });
                }
              }}
            >
              {webhookMissing && (
                <Row>
                  <Col span={24}>
                    <Alert
                      message="GChat webhook not configured for this project. Please add the webhook URL in the project settings."
                      type="error"
                      showIcon
                    />
                  </Col>
                </Row>
              )}
              <div className="app-builds-section-title">Basic Information</div>
              <Row gutter={16}>
                <Col xs={24} md={6}>
                  <Form.Item
                    label="App Name"
                    name="appName"
                    rules={[
                      {
                        required: true,
                        message: 'Please Select App Name',
                      },
                    ]}
                  >
                    <CommonSelect
                      placeholder="Select App Name"
                      showSearch
                      disabled={!!buildId}
                      query={PROJECT_FOR_RELEASE}
                      variables={
                        !buildId
                          ? {
                            filters: {
                              searchString: appNameSearch,
                              sortField: 'createdOn',
                              sortOrder: SORT_BY.DESC,
                            },
                          }
                          : undefined
                      }
                      onSearch={!buildId ? handleAppNameSearch : undefined}
                      useEffectDeps={!buildId ? [appNameSearch] : []}
                      isDataDependent={!buildId ? [appNameSearch] : []}
                      filterOption={!buildId ? false : undefined}
                      responsePath="projectsForRelease.projects"
                      valuePath="id"
                      labelPath="name"
                      labelInValue
                      fetchPolicy="network-only"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    label="App Type"
                    name="appType"
                    rules={[
                      {
                        required: true,
                        message: 'Please Select App Type',
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select App Type"
                      options={APP_TYPES.filter((t) => t.value !== '')}
                      disabled={!!buildId}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    label="Environment"
                    name="environment"
                    rules={[
                      {
                        required: true,
                        message: 'Please Select Environment',
                      },
                    ]}
                  >
                    <Select
                      disabled={!!buildId}
                      placeholder="Select Environment"
                      options={APP_BUILD_ENVIRONMENTS.filter(
                        (e) => e.value !== '',
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    label="Assignee"
                    name="assigneeId"
                    rules={[
                      {
                        required: true,
                        message: 'Please Select Assignee',
                      },
                    ]}
                  >
                    <CommonSelect
                      placeholder="Select Assignee"
                      showSearch
                      disabled={!!buildId}
                      query={APP_BUILD_ASSIGNEES}
                      responsePath="appBuildAssignees"
                      valuePath="id"
                      labelPath="displayName"
                      getLabel={(item) =>
                        item?.displayName ||
                        `${item?.firstName || ''} ${item?.lastName || ''}`.trim()
                      }
                      labelInValue
                      fetchPolicy="network-only"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} md={6}>
                  <Form.Item
                    label="Requester Name"
                    name="requesterName"
                    rules={[
                      {
                        required: true,
                        message: 'Please Select Requester Name',
                      },
                    ]}
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    label="Email"
                    name="requesterEmail"
                    rules={[
                      {
                        required: true,
                        message: 'Please Select Email',
                      },
                    ]}
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
              </Row>
              <Divider />
              <div className="app-builds-section-title">Android</div>
              <Row gutter={16}>
                <Col xs={24} md={6}>
                  <Form.Item
                    label="Android App Version"
                    name="androidAppVersion"
                    rules={[
                      {
                        pattern: REGEX.RELEASE_NUMBER,
                        message: 'Please enter a valid version! (e.g. 1.8.2)',
                      },
                    ]}
                  >
                    <Input
                      placeholder="e.g. 1.8.2"
                      disabled={!!buildId || isAndroidUnavailable}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    label="Android Version Code"
                    name="androidVersionCode"
                    rules={[
                      {
                        pattern: REGEX.NUMBER,
                        message:
                          'Please enter a valid version code! (e.g. 205)',
                      },
                    ]}
                  >
                    <Input
                      placeholder="e.g. 205"
                      disabled={!!buildId || isAndroidUnavailable}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item label="Android Review Status" name="androidStatus">
                    <Select
                      placeholder="Select Review Status"
                      options={reviewStatusOptions}
                      disabled={isAndroidUnavailable || undefined}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    label="APK Test Link"
                    name="apkTestLink"
                    rules={[
                      {
                        pattern: REGEX.URL,
                        message: 'Please enter a valid URL!',
                      },
                    ]}
                  >
                    <Input
                      placeholder="Paste APK Test Link"
                      disabled={!!buildId || isAndroidUnavailable}
                    />
                  </Form.Item>
                  {buildId && buildData?.apkTestLink && (
                    <div className="link-preview">
                      {renderLink(buildData?.apkTestLink)}
                    </div>
                  )}
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} md={6}>
                  <Form.Item
                    label="AAB Submission Link"
                    name="aabSubmissionLink"
                    rules={[
                      {
                        pattern: REGEX.URL,
                        message: 'Please enter a valid URL!',
                      },
                    ]}
                  >
                    <Input
                      placeholder="Paste AAB Submission Link"
                      disabled={!!buildId || isAndroidUnavailable}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    label="Android Submission Mode"
                    name="androidSubmissionMode"
                  >
                    <Select
                      placeholder="Select Submission Mode"
                      options={SUBMISSION_MODES}
                      disabled={!!buildId || isAndroidUnavailable}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    label="Android Rollout Mode"
                    name="androidRolloutMode"
                  >
                    <Select
                      placeholder="Select Rollout Mode"
                      options={ROLLOUT_MODES}
                      disabled={!!buildId || isAndroidUnavailable}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Divider />
              <div className="app-builds-section-title">iOS</div>
              <Row gutter={16}>
                <Col xs={24} md={6}>
                  <Form.Item
                    label="iOS App Version"
                    name="iosAppVersion"
                    rules={[
                      {
                        pattern: REGEX.RELEASE_NUMBER,
                        message: 'Please enter a valid version! (e.g. 1.8.2)',
                      },
                    ]}
                  >
                    <Input
                      placeholder="e.g. 1.8.2"
                      disabled={!!buildId || isIosUnavailable}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    label="iOS Build Number"
                    name="iosBuildNumber"
                    rules={[
                      {
                        pattern: REGEX.NUMBER,
                        message:
                          'Please enter a valid build number! (e.g. 205)',
                      },
                    ]}
                  >
                    <Input
                      placeholder="e.g. 205"
                      disabled={!!buildId || isIosUnavailable}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item label="iOS Review Status" name="iosStatus">
                    <Select
                      placeholder="Select Review Status"
                      options={reviewStatusOptions}
                      disabled={isIosUnavailable || undefined}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    label="IPA Test Link"
                    name="ipaTestLink"
                    rules={[
                      {
                        pattern: REGEX.URL,
                        message: 'Please enter a valid URL!',
                      },
                    ]}
                  >
                    <Input
                      placeholder="Paste IPA Test Link"
                      disabled={!!buildId || isIosUnavailable}
                    />
                  </Form.Item>
                  {buildId && buildData?.ipaTestLink && (
                    <div className="link-preview">
                      {renderLink(buildData?.ipaTestLink)}
                    </div>
                  )}
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} md={6}>
                  <Form.Item
                    label="iOS Submission Mode"
                    name="iosSubmissionMode"
                  >
                    <Select
                      placeholder="Select Submission Mode"
                      options={SUBMISSION_MODES}
                      disabled={!!buildId || isIosUnavailable}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item label="iOS Rollout Mode" name="iosRolloutMode">
                    <Select
                      placeholder="Select Rollout Mode"
                      options={ROLLOUT_MODES}
                      disabled={!!buildId || isIosUnavailable}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Divider />
              <div className="app-builds-section-title">
                Additional Information
              </div>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Google Drive Folder Link"
                    name="googleDriveFolderLink"
                    rules={[
                      {
                        pattern: REGEX.URL,
                        message: 'Please enter a valid URL!',
                      },
                    ]}
                  >
                    <Input
                      placeholder="Paste Google Drive Folder Link"
                      disabled={!!buildId}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item label="What's New Copy" name="whatsNewCopy">
                    <TextArea rows={3} placeholder="App Store Release Text" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item label="Release Notes" name="releaseNotes">
                    <TextArea
                      rows={3}
                      placeholder="ClickUp Task URLs Associated With This Build"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item
                    label="Reviewer Credentials"
                    name="reviewerCredentials"
                  >
                    <MaskedCredentialsInput buildId={buildId} />
                  </Form.Item>
                </Col>
              </Row>
              {buildId && (buildData?.androidStatus || buildData?.iosStatus) ? (
                <>
                  <div className="app-builds-section-title">Current Status</div>
                  <Row gutter={16}>
                    {buildData?.androidStatus && (
                      <Col xs={12} md={6}>
                        <span>Android: </span>
                        <StatusBadge status={buildData?.androidStatus} />
                      </Col>
                    )}
                    {buildData?.iosStatus && (
                      <Col xs={12} md={6}>
                        <span>iOS: </span>
                        <StatusBadge status={buildData?.iosStatus} />
                      </Col>
                    )}
                  </Row>
                </>
              ) : null}
            </Form>
          </div>
        </div>
      </Card>
    </>
  );
}

export default ViewAppBuild;
